import React, { useState, useMemo, useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getStatusBackgroundColor,
  getContrastTextColor,
  sortAppointmentsByDateTimeCompletedLast,
} from '../../utils/appointmentUtils';
import { isUpcomingAttentionNeeded } from '../../utils/appointmentWorkflow';
import { getCompanyAppointmentUrlSegment } from '../../shared/booking/appointments/companyAppointmentRouting';
import BackButton from '../../shared/components/back-button/BackButton';
import { AppointmentPaymentPill, AppointmentStatusPill } from '../overview/AppointmentStatusPill';
import VirtualFixedList from '../../shared/virtualization/VirtualFixedList';
import './styles/AppointmentsPage.css';

const APPOINTMENTS_ROW_HEIGHT = 88;

const ScheduleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
  </svg>
);

const convertToDate = (dateValue) => {
  if (!dateValue) return null;
  try {
    if (dateValue instanceof Date) return dateValue;
    if (dateValue?.toDate) return dateValue.toDate();
    return new Date(dateValue);
  } catch (error) {
    console.error('Error converting date:', error);
    return null;
  }
};

const filterAppointmentsForTab = (appointments, tabIndex) => {
  if (!appointments?.length) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return sortAppointmentsByDateTimeCompletedLast(
    appointments.filter((appointment) => {
      if (!appointment?.dateTime) return false;
      const appointmentDate = convertToDate(appointment.dateTime);
      if (!appointmentDate) return false;

      switch (tabIndex) {
        case 0:
          return true;
        case 1:
          return appointmentDate >= today && appointmentDate < tomorrow;
        case 2:
          return isUpcomingAttentionNeeded(appointment);
        default:
          return false;
      }
    }),
    convertToDate
  );
};

const AppointmentsPage = ({
  appointments = [],
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(location.state?.initialTab || 0);

  const handleAppointmentClick = useCallback((appointment) => {
    const appointmentSegment = getCompanyAppointmentUrlSegment(appointment);
    if (!appointmentSegment) return;
    navigate(`/app/overview/appointment/${appointmentSegment}`, {
      state: { appointment }
    });
  }, [navigate]);

  const getServiceDisplay = useCallback((appointment) => {
    if (!appointment) return 'No service specified';
    const { service, serviceName } = appointment;
    return service?.name || serviceName || 'No service specified';
  }, []);

  const filteredByTab = useMemo(
    () => [0, 1, 2].map((tabIndex) => filterAppointmentsForTab(appointments, tabIndex)),
    [appointments]
  );

  const handlers = useSwipeable({
    onSwipedLeft: () => { if (currentTab < 2) setCurrentTab(currentTab + 1); },
    onSwipedRight: () => { if (currentTab > 0) setCurrentTab(currentTab - 1); },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  const tabs = [
    { label: 'All', index: 0 },
    { label: 'Today', index: 1 },
    { label: 'Needs attention', index: 2 }
  ];

  const renderAppointmentRow = useCallback((appointment) => (
    <button
      type="button"
      className="appointments-page__item"
      onClick={() => handleAppointmentClick(appointment)}
    >
      <span
        className="appointments-page__avatar"
        style={{
          backgroundColor: getStatusBackgroundColor(appointment),
          color: getContrastTextColor()
        }}
      >
        <ScheduleIcon />
      </span>
      <span className="appointments-page__body">
        <span className="appointments-page__row">
          <span className="appointments-page__client">
            {appointment.clientName}
          </span>
          <span className="appointments-page__badges">
            <AppointmentStatusPill appointment={appointment} />
            <AppointmentPaymentPill appointment={appointment} />
          </span>
        </span>
        <p className="appointments-page__service">
          {getServiceDisplay(appointment)}
        </p>
        <p className="appointments-page__date">
          {format(convertToDate(appointment.dateTime), 'PPp')}
        </p>
      </span>
    </button>
  ), [getServiceDisplay, handleAppointmentClick]);

  return (
    <div className="appointments-page">
      <header className="appointments-page__header">
        <BackButton
          label="Back to Overview"
          className="back-btn--flush"
          onClick={() => navigate('/app/overview')}
        />
        <ul className="appointments-page__tabs" role="tablist">
          {tabs.map(({ label, index }) => (
            <li key={index} className="appointments-page__tab" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={currentTab === index}
                aria-controls={`appointments-panel-${index}`}
                id={`appointments-tab-${index}`}
                className="appointments-page__tab-btn"
                onClick={() => setCurrentTab(index)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      </header>

      <div className="appointments-page__body" {...handlers}>
        {tabs.map(({ index }) => {
          const tabAppointments = filteredByTab[index] || [];

          return (
            <div
              key={index}
              id={`appointments-panel-${index}`}
              role="tabpanel"
              aria-labelledby={`appointments-tab-${index}`}
              hidden={currentTab !== index}
              className={`appointments-page__panel ${currentTab === index ? 'is-active' : ''}`}
            >
              <div className="appointments-page__list-wrap">
                {tabAppointments.length > 0 ? (
                  <VirtualFixedList
                    className="appointments-page__virtual-list"
                    listClassName="appointments-page__list"
                    items={tabAppointments}
                    itemHeight={APPOINTMENTS_ROW_HEIGHT}
                    itemGap={0}
                    renderItem={renderAppointmentRow}
                    ariaLabel={`${tabs[index].label} appointments`}
                    emptyFallback={(
                      <p className="appointments-page__empty">No appointments found</p>
                    )}
                  />
                ) : (
                  <p className="appointments-page__empty">No appointments found</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppointmentsPage;
